import {
  Fragment,
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
  ReactPortal,
} from "react";
import Banner from "../layout/Banner";
import { Footer } from "../landing/Footer";
import TopNavbar from "../landing/TopNavbar";
import CallToAction from "../layout/CallToAction";
import getButtonStyle from "../../utils/styles";
import { CheckIcon, MinusIcon } from "@heroicons/react/24/outline";

interface Feature {
  name: string;
  tiers: [
    { name: "Control"; value: null | boolean | string },
    { name: "Concierge"; value: null | boolean | string },
    { name: "Hub"; value: null | boolean | string },
  ];
}

interface Section {
  name: string;
  features: Array<Feature>;
}

const pricing: any = [
  {
    industry: "Hospitality",
    title:
      "These packages and features apply if you're a property or venue within the hospitality industry.",
    tiers: [
      {
        name: "Control",
        id: "tier-control",
        href: "https://audico.co.uk/contact",
        priceMonthly: "£12.99",
        description:
          "Enables your guests to control their environment such as lighting, heating & audio.",
        mostPopular: false,
      },
      {
        name: "Concierge",
        id: "tier-concierge",
        href: "https://audico.co.uk/contact",
        priceMonthly: "£14.99",
        description:
          "A fully integrated self service concierge system with customisable features and branding.",
        mostPopular: false,
      },
      {
        name: "Hub",
        id: "tier-hub",
        href: "https://audico.co.uk/contact",
        priceMonthly: "£20.99",
        description:
          "Access to exclusive content as well as our fully integrated Pay-by-Voice system.",
        mostPopular: true,
      },
    ],
    sections: [
      {
        name: "Dashboard",
        features: [
          {
            name: "Real time updates",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Room administration",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Device administration",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Graphical branding",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Analytics",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Audit log",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Instant & scheduled reminders",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Video & voice calling",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Content management",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Resident orientation",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Custom integration",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Feedback channels",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
        ],
      },
      {
        name: "Environmental controls",
        features: [
          {
            name: "Lighting",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Heating",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Television",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Blinds",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Air conditioning",
            tiers: [
              { name: "Control", value: true },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Advanced controls",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
        ],
      },
      {
        name: "Concierge",
        features: [
          {
            name: "Food ordering",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Drink ordering",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Bookings",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Local information",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Schedule updates",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Content management",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
        ],
      },
      {
        name: "Payments",
        features: [
          {
            name: "Point-of-sale interface",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Integrated payment gateway",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Create a tab",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Payment handoff",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: true },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Automated receipts",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Real time notification",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
        ],
      },
      {
        name: "Experience",
        features: [
          {
            name: "Exclusive audio content",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Exclusive video content",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Custom broadcasts",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
          {
            name: "Immersive experiences",
            tiers: [
              { name: "Control", value: false },
              { name: "Concierge", value: false },
              { name: "Hub", value: true },
            ],
          },
        ],
      },
      {
        name: "Support",
        features: [
          {
            name: "Included level of support",
            tiers: [
              { name: "Control", value: "Basic" },
              { name: "Concierge", value: "Standard" },
              { name: "Hub", value: "Premium" },
            ],
          },
        ],
      },
    ],
  },
];

const support: any = [
  {
    tiers: [
      {
        name: "Basic",
        id: "tier-basic-support",
        href: "https://audico.co.uk/contact",
        priceMonthly: "0",
        description: "",
        mostPopular: false,
      },
      {
        name: "Standard",
        id: "tier-standard-support",
        href: "https://audico.co.uk/contact",
        priceMonthly: "£5.99",
        description: "",
        mostPopular: true,
      },
      {
        name: "Premium",
        id: "tier-premium-support",
        href: "https://audico.co.uk/contact",
        priceMonthly: "£11.99",
        description: "",
        mostPopular: false,
      },
    ],
    sections: [
      {
        name: "Contact",
        features: [
          {
            name: "Create support tickets",
            tiers: [
              { name: "Basic", value: true },
              { name: "Standard", value: true },
              { name: "Premium", value: true },
            ],
          },
          {
            name: "24/7 email support",
            tiers: [
              { name: "Basic", value: true },
              { name: "Standard", value: true },
              { name: "Premium", value: true },
            ],
          },
          {
            name: "24/7 phone support",
            tiers: [
              { name: "Basic", value: false },
              { name: "Standard", value: true },
              { name: "Premium", value: true },
            ],
          },
        ],
      },
      {
        name: "Expertise",
        features: [
          {
            name: "Project roadmap",
            tiers: [
              { name: "Basic", value: false },
              { name: "Standard", value: true },
              { name: "Premium", value: true },
            ],
          },
          {
            name: "Dedicated project manager",
            tiers: [
              { name: "Basic", value: false },
              { name: "Standard", value: false },
              { name: "Premium", value: true },
            ],
          },
          {
            name: "Custom development",
            tiers: [
              { name: "Basic", value: "Charged per requirements" },
              { name: "Standard", value: "Charged per requirements" },
              { name: "Premium", value: "Discounted rate" },
            ],
          },
        ],
      },
    ],
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

function buildFeature(feature: Feature, name: string) {
  const tiers = feature.tiers;
  const filteredTiers = tiers.filter((tier) => tier.name === name);
  if (filteredTiers.length === 0)
    return (
      <CheckIcon className="h-6 w-5 flex-none text-info" aria-hidden="true" />
    );
  const tier = filteredTiers[0];
  if (typeof tier.value === "string")
    return (
      <span className="text-sm leading-6 text-gray-500">({tier.value})</span>
    );
  if (tier.value === true) {
    return (
      <CheckIcon className="h-6 w-5 flex-none text-info" aria-hidden="true" />
    );
  } else {
    return (
      <MinusIcon
        className="h-6 w-5 flex-none text-gray-400"
        aria-hidden="true"
      />
    );
  }
}

function buildFeatureLarge(feature: Feature, name: string) {
  const tiers = feature.tiers;
  const filteredTiers = tiers.filter((tier) => tier.name === name);
  if (filteredTiers.length === 0) return null;
  const tier = filteredTiers[0];
  if (tier.value === null) return null;
  if (typeof tier.value === "string")
    return (
      <div className="text-center text-sm leading-6 text-light">
        {tier.value}
      </div>
    );
  if (tier.value === true) {
    return (
      <>
        <CheckIcon className="mx-auto h-5 w-5 text-info" aria-hidden="true" />
        <span className="sr-only">{`Included in ${tier.name}`}</span>
      </>
    );
  } else {
    return (
      <>
        <MinusIcon className="mx-auto h-5 w-5 text-light" aria-hidden="true" />
        <span className="sr-only">{`Not included in ${tier.name}`}</span>
      </>
    );
  }
}

export default function Pricing() {
  return (
    <div className="bg-midnight">
      <TopNavbar />

      {/* Pricing - subscription */}
      <div className="mx-auto max-w-7xl px-6 pt-2 sm:pt-4 pb-16 sm:pb-20">
        {/* <div className="mx-auto max-w-4xl text-center mt-0 lg:mt-20">
          <h2 className="text-4xl font-bold leading-7 text-info">Pricing</h2>
        </div> */}

        {pricing.map((industry: any, idx: number) => (
          <div className="pt-20" key={idx} id="industry-pricing-sections">
            {/* xs to lg */}
            <div className="mx-auto mt-8 max-w-md space-y-8 sm:mt-10 lg:hidden">
              {industry.tiers.map((tier: any) => (
                <section
                  key={tier.id}
                  className={classNames(
                    tier.mostPopular ? "rounded-xl bg-light/20" : "",
                    "p-8",
                  )}
                >
                  <h3
                    id={tier.id}
                    className="text-sm font-semibold leading-6 text-white"
                  >
                    {tier.name}
                  </h3>
                  <span className="mt-2 text-xs text-light">Starting from</span>
                  <p className="flex items-baseline gap-x-1 text-white">
                    <span className="text-4xl font-bold">
                      {tier.priceMonthly}
                    </span>
                    <span className="text-sm font-semibold">/month</span>
                  </p>
                  <a
                    href={tier.href}
                    aria-describedby={tier.id}
                    className={classNames(
                      tier.mostPopular
                        ? "bg-info text-white hover:bg-info-dark"
                        : "text-light ring-1 ring-inset ring-light hover:ring-2",
                      "mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info",
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Get in touch
                  </a>
                  <ul className="mt-10 space-y-4 text-sm leading-6 text-light">
                    {industry.sections.map((section: Section) => (
                      <li key={section.name}>
                        <ul className="space-y-4">
                          {section.features.map((feature: Feature) => (
                            <li key={feature.name} className="flex gap-x-3">
                              {feature.name}{" "}
                              <span className="ml-auto">
                                {buildFeature(feature, tier.name)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {/* lg+ */}
            <div className="isolate mt-12 hidden lg:block">
              <div className="relative -mx-8">
                {industry.tiers.some(
                  (tier: { mostPopular: any }) => tier.mostPopular,
                ) ? (
                  <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
                    <div
                      className={`flex w-1/${
                        industry.industry === "Home" ? 3 : 4
                      } px-4`}
                      aria-hidden="true"
                      style={{
                        marginLeft:
                          industry.industry === "Home"
                            ? `${
                                (industry.tiers.findIndex(
                                  (tier: { mostPopular: any }) =>
                                    tier.mostPopular,
                                ) +
                                  1) *
                                33
                              }%`
                            : `${
                                (industry.tiers.findIndex(
                                  (tier: { mostPopular: any }) =>
                                    tier.mostPopular,
                                ) +
                                  1) *
                                25
                              }%`,
                      }}
                    >
                      <div className="w-full rounded-t-xl border-x border-t border-none bg-light/20" />
                    </div>
                  </div>
                ) : null}
                <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
                  <caption className="sr-only">Pricing plan comparison</caption>
                  <colgroup>
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                  </colgroup>
                  <thead>
                    <tr>
                      <td />
                      {industry.tiers.map(
                        (tier: {
                          id: Key | null | undefined;
                          name:
                            | string
                            | number
                            | boolean
                            | ReactElement<
                                any,
                                string | JSXElementConstructor<any>
                              >
                            | Iterable<ReactNode>
                            | ReactPortal
                            | null
                            | undefined;
                        }) => (
                          <th
                            key={tier.id}
                            scope="col"
                            className="px-6 pt-6 xl:px-8 xl:pt-8"
                          >
                            <div className="text-sm font-semibold leading-7 text-white">
                              {tier.name}
                            </div>
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">
                        <span className="sr-only">Price</span>
                      </th>
                      {industry.tiers.map(
                        (tier: {
                          id: Key | null | undefined;
                          priceMonthly:
                            | string
                            | number
                            | boolean
                            | ReactElement<
                                any,
                                string | JSXElementConstructor<any>
                              >
                            | Iterable<ReactNode>
                            | ReactPortal
                            | null
                            | undefined;
                          href: string | undefined;
                          mostPopular: any;
                        }) => (
                          <td key={tier.id} className="px-6 pt-2 xl:px-8">
                            <span className="text-xs text-light">
                              Starting from
                            </span>
                            <div className="flex items-baseline gap-x-1 text-white">
                              <span className="text-4xl font-bold">
                                {tier.priceMonthly}
                              </span>
                              <span className="text-sm font-semibold leading-6">
                                /month
                              </span>
                            </div>
                            <a
                              href={tier.href}
                              className={classNames(
                                tier.mostPopular
                                  ? "bg-info text-white hover:bg-info-dark"
                                  : "text-light ring-1 ring-inset ring-light hover:ring-2",
                                "mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info",
                              )}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Get in touch
                            </a>
                          </td>
                        ),
                      )}
                    </tr>
                    {industry.sections.map(
                      (section: any, sectionIdx: number) => (
                        <Fragment key={section.name}>
                          <tr>
                            <th
                              scope="colgroup"
                              colSpan={4}
                              className={classNames(
                                sectionIdx === 0 ? "pt-8" : "pt-16",
                                "pb-4 text-sm font-semibold leading-6 text-white",
                              )}
                            >
                              {section.name}
                              <div className="absolute inset-x-8 mt-4 h-px bg-light/20" />
                            </th>
                          </tr>
                          {section.features.map((feature: Feature) => (
                            <tr key={feature.name}>
                              <th
                                scope="row"
                                className="py-4 text-sm font-normal leading-6 text-light"
                              >
                                {feature.name}
                                <div className="absolute inset-x-8 mt-4 h-px bg-light/5" />
                              </th>
                              {industry.tiers.map(
                                (tier: {
                                  id: Key | null | undefined;
                                  name: string;
                                }) => (
                                  <td
                                    key={tier.id}
                                    className="px-6 py-4 xl:px-8"
                                  >
                                    {buildFeatureLarge(feature, tier.name)}
                                  </td>
                                ),
                              )}
                            </tr>
                          ))}
                        </Fragment>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Support */}
      <div className="mx-auto max-w-7xl px-6 pt-16 sm:pt-20 pb-28 sm:pb-32">
        <div className="mx-auto max-w-4xl text-center mt-0 lg:mt-20">
          <h2 className="text-4xl font-bold leading-7 text-info">Support</h2>
          <p className="mt-4 text-light">
            If you need ongoing support, we can help you whatever your needs are
          </p>
        </div>

        {support.map((spt: any, idx: number) => (
          <div key={idx} className="pt-20">
            {/* xs to lg */}
            <div className="mx-auto mt-8 max-w-md space-y-8 sm:mt-10 lg:hidden">
              {spt.tiers.map((tier: any) => (
                <section
                  key={tier.id}
                  className={classNames(
                    tier.mostPopular ? "rounded-xl bg-light/20" : "",
                    "p-8",
                  )}
                >
                  <h3
                    id={tier.id}
                    className="text-sm font-semibold leading-6 text-white"
                  >
                    {tier.name}
                  </h3>
                  <span className="text-xs mt-2 text-light">Starting from</span>
                  <p className="flex items-baseline gap-x-1 text-white">
                    <span className="text-4xl font-bold">
                      {tier.priceMonthly}
                    </span>
                    <span className="text-sm font-semibold">/month</span>
                  </p>
                  <a
                    href={tier.href}
                    aria-describedby={tier.id}
                    className={classNames(
                      tier.mostPopular
                        ? "bg-info text-white hover:bg-info-dark"
                        : "text-light ring-1 ring-inset ring-light hover:ring-2",
                      "mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info",
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Get in touch
                  </a>
                  <ul className="mt-10 space-y-4 text-sm leading-6 text-light">
                    {spt.sections.map((section: Section) => (
                      <li key={section.name}>
                        <ul className="space-y-4">
                          {section.features.map((feature: Feature) => (
                            <li key={feature.name} className="flex gap-x-3">
                              {feature.name}{" "}
                              <span className="ml-auto">
                                {buildFeature(feature, tier.name)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {/* lg+ */}
            <div className="isolate mt-12 hidden lg:block">
              <div className="relative -mx-8">
                {spt.tiers.some(
                  (tier: { mostPopular: any }) => tier.mostPopular,
                ) ? (
                  <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
                    <div
                      className={`flex w-1/4 px-4`}
                      aria-hidden="true"
                      style={{
                        marginLeft: `${
                          (spt.tiers.findIndex(
                            (tier: { mostPopular: any }) => tier.mostPopular,
                          ) +
                            1) *
                          25
                        }%`,
                      }}
                    >
                      <div className="w-full rounded-t-xl border-x border-t border-none bg-light/20" />
                    </div>
                  </div>
                ) : null}
                <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
                  <caption className="sr-only">Pricing plan comparison</caption>
                  <colgroup>
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                    <col className="w-1/4" />
                  </colgroup>
                  <thead>
                    <tr>
                      <td />
                      {spt.tiers.map(
                        (tier: {
                          id: Key | null | undefined;
                          name:
                            | string
                            | number
                            | boolean
                            | ReactElement<
                                any,
                                string | JSXElementConstructor<any>
                              >
                            | Iterable<ReactNode>
                            | ReactPortal
                            | null
                            | undefined;
                        }) => (
                          <th
                            key={tier.id}
                            scope="col"
                            className="px-6 pt-6 xl:px-8 xl:pt-8"
                          >
                            <div className="text-sm font-semibold leading-7 text-white">
                              {tier.name}
                            </div>
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">
                        <span className="sr-only">Price</span>
                      </th>
                      {spt.tiers.map(
                        (tier: {
                          id: Key | null | undefined;
                          priceMonthly:
                            | string
                            | number
                            | boolean
                            | ReactElement<
                                any,
                                string | JSXElementConstructor<any>
                              >
                            | Iterable<ReactNode>
                            | ReactPortal
                            | null
                            | undefined;
                          href: string | undefined;
                          mostPopular: any;
                        }) => (
                          <td key={tier.id} className="px-6 pt-2 xl:px-8">
                            <span className="text-xs text-light">
                              Starting from
                            </span>
                            <div className="flex items-baseline gap-x-1 text-white">
                              <span className="text-4xl font-bold">
                                {tier.priceMonthly}
                              </span>
                              <span className="text-sm font-semibold leading-6">
                                /month
                              </span>
                            </div>
                            <a
                              href={tier.href}
                              className={classNames(
                                tier.mostPopular
                                  ? "bg-info text-white hover:bg-info-dark"
                                  : "text-light ring-1 ring-inset ring-light hover:ring-2",
                                "mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info",
                              )}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Get in touch
                            </a>
                          </td>
                        ),
                      )}
                    </tr>
                    {spt.sections.map((section: any, sectionIdx: number) => (
                      <Fragment key={section.name}>
                        <tr>
                          <th
                            scope="colgroup"
                            colSpan={4}
                            className={classNames(
                              sectionIdx === 0 ? "pt-8" : "pt-16",
                              "pb-4 text-sm font-semibold leading-6 text-light",
                            )}
                          >
                            {section.name}
                            <div className="absolute inset-x-8 mt-4 h-px bg-light/20" />
                          </th>
                        </tr>
                        {section.features.map((feature: Feature) => (
                          <tr key={feature.name}>
                            <th
                              scope="row"
                              className="py-4 text-sm font-normal leading-6 text-light"
                            >
                              {feature.name}
                              <div className="absolute inset-x-8 mt-4 h-px bg-light/5" />
                            </th>
                            {spt.tiers.map(
                              (tier: {
                                id: Key | null | undefined;
                                name: string;
                              }) => (
                                <td key={tier.id} className="px-6 py-4 xl:px-8">
                                  {buildFeatureLarge(feature, tier.name)}
                                </td>
                              ),
                            )}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Call to action */}
      <CallToAction bgColour="bg-midnight">
        <div className="mx-auto max-w-xl lg:max-w-3xl text-center">
          <h2 className="font-display tracking-tight text-primarygotham-bold text-3xl sm:text-4xl md:text-5xl gotham-bold text-primary">
            Are you ready for next steps?
          </h2>
          <p className="mt-4 text-lg tracking-tight text-slate-200">
            If you've found a suitable pricing plan or would like something more
            bespoke, get in touch with us today!
          </p>
          <a
            className={`${getButtonStyle("solid", "info")} mt-10`}
            href="https://audico.co.uk/contact"
            target="_blank"
            rel="noreferrer"
          >
            I'm ready for the next step
          </a>
        </div>
      </CallToAction>

      {/* Footer */}
      <Footer />

      {/* Alert banner */}
      <Banner />
    </div>
  );
}
